import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import InterventionForm from '../components/interventions/InterventionForm';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { Calendar, ChevronLeft, ChevronRight, AlertCircle, Plus, Clock, MapPin } from 'lucide-react';
import api from '../services/api';
import { format, addDays, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const PlanningPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('day');
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const { isCoordinatrice } = useAuth();

  useEffect(() => {
    loadInterventions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, viewMode]);

  const loadInterventions = async () => {
    setLoading(true);
    try {
      let params = {};
      if (viewMode === 'day') {
        params.date = format(selectedDate, 'yyyy-MM-dd');
      } else {
        const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
        const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
        params.debut = format(start, 'yyyy-MM-dd');
        params.fin = format(end, 'yyyy-MM-dd');
      }

      const response = await api.get('/interventions', { params });
      setInterventions(response.data);
    } catch (error) {
      console.error('Erreur chargement interventions:', error);
    } finally {
      setLoading(false);
    }
  };

  const goToPrevious = () => {
    setSelectedDate(prev => addDays(prev, viewMode === 'day' ? -1 : -7));
  };

  const goToNext = () => {
    setSelectedDate(prev => addDays(prev, viewMode === 'day' ? 1 : 7));
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const getVigilanceColor = (niveau) => {
    const colors = {
      vert: 'bg-emerald-500',
      orange: 'bg-orange-500',
      rouge: 'bg-red-500'
    };
    return colors[niveau] || 'bg-gray-400';
  };

  const getVigilanceBadge = (niveau) => {
    const badges = {
      vert: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Normal' },
      orange: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', label: 'Attention' },
      rouge: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Urgent' }
    };
    return badges[niveau] || { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200', label: 'N/A' };
  };

  const groupByDate = (interventions) => {
    const grouped = {};
    interventions.forEach(intervention => {
      const dateKey = format(new Date(intervention.date), 'yyyy-MM-dd');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(intervention);
    });
    return grouped;
  };

  if (loading) {
    return <LoadingSpinner fullScreen />;
  }

  const groupedInterventions = viewMode === 'week' ? groupByDate(interventions) : null;

  return (
    <div className="pb-20 bg-gradient-to-br from-blue-50 via-white to-purple-50 min-h-screen">
      {/* Header moderne avec navigation */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white p-6 rounded-b-[2rem] shadow-xl sticky top-0 z-10">
        {/* Navigation date */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={goToPrevious} 
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-2 rounded-xl transition-all active:scale-95"
          >
            <ChevronLeft size={24} />
          </button>
          
          <div className="text-center flex-1">
            <h1 className="text-xl font-bold drop-shadow-sm">
              {viewMode === 'day' 
                ? format(selectedDate, 'EEEE d MMMM', { locale: fr })
                : `Semaine du ${format(startOfWeek(selectedDate, { weekStartsOn: 1 }), 'd MMM', { locale: fr })}`
              }
            </h1>
            <p className="text-sm text-blue-100 font-medium">
              📋 {interventions.length} intervention{interventions.length > 1 ? 's' : ''}
            </p>
          </div>
          
          <button 
            onClick={goToNext} 
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm p-2 rounded-xl transition-all active:scale-95"
          >
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Boutons de contrôle */}
        <div className="grid grid-cols-2 gap-2">
          <button 
            onClick={goToToday} 
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl py-2.5 text-sm font-bold transition-all active:scale-95"
          >
            📅 Aujourd'hui
          </button>
          <button 
            onClick={() => setViewMode(viewMode === 'day' ? 'week' : 'day')}
            className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl py-2.5 text-sm font-bold transition-all active:scale-95"
          >
            {viewMode === 'day' ? '📆 Vue semaine' : '📋 Vue jour'}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Bouton créer intervention (coordinatrices) */}
        {isCoordinatrice && (
          <button
            onClick={() => setModalOpen(true)}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 transition-all active:scale-95"
          >
            <Plus size={24} />
            Planifier une intervention
          </button>
        )}

        {viewMode === 'day' ? (
          // Vue jour
          <div className="space-y-4">
            {interventions.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 text-center">
                <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Calendar size={40} className="text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">Aucune intervention ce jour</p>
                <p className="text-sm text-gray-400 mt-1">Profitez de cette journée calme 😊</p>
              </div>
            ) : (
              interventions
                .sort((a, b) => a.heureDebut.localeCompare(b.heureDebut))
                .map((intervention) => {
                  const vigilanceBadge = getVigilanceBadge(intervention.beneficiaireId?.niveauVigilance);
                  
                  return (
                    <div
                      key={intervention._id}
                      onClick={() => navigate(`/intervention/${intervention._id}`)}
                      className="bg-white rounded-2xl shadow-md hover:shadow-xl border border-gray-100 p-5 cursor-pointer transform hover:scale-[1.02] transition-all duration-200"
                    >
                      {/* En-tête */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start gap-3 flex-1">
                          {/* Indicateur vigilance */}
                          <div className={`w-1 h-full rounded-full ${getVigilanceColor(intervention.beneficiaireId?.niveauVigilance)}`}></div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-lg text-gray-800">
                                {intervention.beneficiaireId?.prenom} {intervention.beneficiaireId?.nom}
                              </h3>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${vigilanceBadge.bg} ${vigilanceBadge.text} border ${vigilanceBadge.border}`}>
                                {vigilanceBadge.label}
                              </span>
                            </div>
                            <p className="text-sm text-gray-500 font-medium">{intervention.type}</p>
                          </div>
                        </div>

                        {/* Horaires */}
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl px-3 py-2 text-center border border-blue-100 ml-3">
                          <p className="font-bold text-blue-700 text-sm">{intervention.heureDebut}</p>
                          <p className="text-xs text-gray-500">à</p>
                          <p className="font-bold text-purple-700 text-sm">{intervention.heureFin}</p>
                        </div>
                      </div>
                      
                      {/* Adresse */}
                      <div className="flex items-start gap-2 text-sm text-gray-600 mb-3 bg-gray-50 rounded-lg p-2">
                        <MapPin size={16} className="text-blue-500 flex-shrink-0 mt-0.5" />
                        <span className="font-medium">
                          {intervention.beneficiaireId?.adresse?.rue}
                        </span>
                      </div>

                      {/* Consignes */}
                      {intervention.beneficiaireId?.consignes && (
                        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-amber-400 rounded-lg p-3 flex gap-2 mb-3">
                          <AlertCircle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-800 line-clamp-2 leading-relaxed">
                            {intervention.beneficiaireId.consignes}
                          </p>
                        </div>
                      )}

                      {/* Statut */}
                      <div className="flex gap-2">
                        {intervention.statut === 'effectuee' && (
                          <span className="inline-flex items-center gap-1 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
                            <span>✓</span>
                            <span>Effectuée</span>
                          </span>
                        )}
                        {intervention.statut === 'planifiee' && (
                          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold border border-blue-200">
                            <Clock size={12} />
                            <span>Planifiée</span>
                          </span>
                        )}
                        {intervention.statut === 'arrivee' && (
                          <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 px-3 py-1 rounded-full text-xs font-bold border border-orange-200">
                            <span>🚗</span>
                            <span>En cours</span>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        ) : (
          // Vue semaine
          <div className="space-y-5">
            {Object.keys(groupedInterventions).length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 text-center">
                <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Calendar size={40} className="text-gray-400" />
                </div>
                <p className="text-gray-600 font-medium">Aucune intervention cette semaine</p>
                <p className="text-sm text-gray-400 mt-1">Profitez de cette semaine calme 😊</p>
              </div>
            ) : (
              Object.entries(groupedInterventions)
                .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                .map(([date, dayInterventions]) => (
                  <div key={date}>
                    {/* En-tête jour */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl p-2">
                        <Calendar size={18} className="text-white" />
                      </div>
                      <h3 className="text-base font-bold text-gray-800">
                        {format(new Date(date), 'EEEE d MMMM', { locale: fr })}
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          ({dayInterventions.length})
                        </span>
                      </h3>
                    </div>

                    {/* Interventions du jour */}
                    <div className="space-y-3 ml-2">
                      {dayInterventions
                        .sort((a, b) => a.heureDebut.localeCompare(b.heureDebut))
                        .map((intervention) => {
                          const vigilanceBadge = getVigilanceBadge(intervention.beneficiaireId?.niveauVigilance);
                          
                          return (
                            <div
                              key={intervention._id}
                              onClick={() => navigate(`/intervention/${intervention._id}`)}
                              className="bg-white rounded-xl shadow-md hover:shadow-lg border border-gray-100 p-4 cursor-pointer transition-all"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-2 h-2 rounded-full ${getVigilanceColor(intervention.beneficiaireId?.niveauVigilance)}`}></div>
                                <div className="flex-1">
                                  <div className="flex justify-between items-center">
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <p className="font-bold text-gray-800">
                                          {intervention.beneficiaireId?.prenom} {intervention.beneficiaireId?.nom}
                                        </p>
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${vigilanceBadge.bg} ${vigilanceBadge.text}`}>
                                          {vigilanceBadge.label}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-500 mt-1">{intervention.type}</p>
                                    </div>
                                    <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white font-bold px-3 py-1 rounded-lg shadow-sm">
                                      {intervention.heureDebut}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))
            )}
          </div>
        )}
      </div>

      {/* Modal création intervention */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title="Planifier une intervention"
        size="md"
      >
        <InterventionForm
          preSelectedDate={selectedDate}
          onSuccess={() => {
            setModalOpen(false);
            loadInterventions();
          }}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
};

export default PlanningPage;
